import clsx from "clsx";
import axios from "axios";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useCart } from "../Context/Createcontext";
import { Helmet } from "react-helmet";
import { api_url } from "../config/config";

const plusPattern = `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='17' y='10' width='6' height='20' rx='2' fill='white'/%3E%3Crect x='10' y='17' width='20' height='6' rx='2' fill='white'/%3E%3C/svg%3E")`;

const STATUS_CONFIG = {
  ORDER_PLACED:      { label: "Order Placed",      color: "bg-blue-50 text-blue-700 border-blue-200",        dot: "bg-blue-500" },
  ORDER_RECEIVED:    { label: "Order Received",     color: "bg-cyan-50 text-cyan-700 border-cyan-200",        dot: "bg-cyan-500" },
  ORDER_PROCESSING:  { label: "Processing",         color: "bg-amber-50 text-amber-700 border-amber-200",     dot: "bg-amber-500" },
  OUT_FOR_DELIVERY:  { label: "Out for Delivery",   color: "bg-purple-50 text-purple-700 border-purple-200",  dot: "bg-purple-500" },
  DELIVERED:         { label: "Delivered",          color: "bg-[#eaf4f3] text-[#1e4a46] border-[#d4ecea]",   dot: "bg-[#5fbdb3]" },
  CANCELLED:         { label: "Cancelled",          color: "bg-red-50 text-red-600 border-red-200",           dot: "bg-red-500" },
  FAILED:            { label: "Failed",             color: "bg-red-50 text-red-600 border-red-200",           dot: "bg-red-500" },
};

const MyOrders = () => {
  const [orders, setOrders] = useState([]);
  const { setCartCount } = useCart();
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const [expandedOrders, setExpandedOrders] = useState([]);
  const [visibleOrders, setVisibleOrders] = useState(10);
  const [ratings, setRatings] = useState({});
  const [reviews, setReviews] = useState({});
  const [openReview, setOpenReview] = useState({});
  const [submitted, setSubmitted] = useState({});
  const [statusFilterUI, setStatusFilterUI] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const statusOptions = [
    { value: "", label: "All Orders" },
    { value: "ORDER_PLACED", label: "Order Placed" },
    { value: "ORDER_RECEIVED", label: "Order Received" },
    { value: "ORDER_PROCESSING", label: "Processing" },
    { value: "OUT_FOR_DELIVERY", label: "Out for Delivery" },
    { value: "DELIVERED", label: "Delivered" },
    { value: "FAILED", label: "Failed" },
    { value: "CANCELLED", label: "Cancelled" },
  ];

  const getUserOrders = async () => {
    setLoading(true);
    try {
      const url = statusFilterUI ? `${api_url}/user-orders?deliveryStatus=${statusFilterUI}` : `${api_url}/user-orders`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.data?.data) { setOrders([]); return; }
      const orderData = response.data.data;
      const orderDetails = Array.isArray(orderData) ? orderData.filter(i => i.details?.length > 0) : orderData.details?.length > 0 ? [orderData] : [];
      setOrders(orderDetails.filter(o => o.details[0].delivery_status !== "PENDING"));
    } catch(e) { console.error("Error fetching user orders:", e); }
    finally { setLoading(false); }
  };
  useEffect(() => { getUserOrders(); }, [statusFilterUI]);

  const toggleExpand = (id) => setExpandedOrders(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const handleStarClick = (id, val) => setRatings(prev => ({...prev,[id]:val}));
  const handleReviewChange = (id, val) => setReviews(prev => ({...prev,[id]:val}));
  const toggleReviewDropdown = (id) => setOpenReview(prev => ({...prev,[id]:!prev[id]}));

  const submitReview = async (orderId, orderDetails) => {
    try {
      const detailsOrderId = orderDetails?.details?.[0]?.orderId;
      if (!detailsOrderId) throw new Error("Order ID not found");
      const rating = ratings[orderDetails.id];
      const review = reviews[orderDetails.id]?.trim();
      if (!rating) throw new Error("Please select a rating");
      if (!review) throw new Error("Please enter a review");
      const response = await axios.post(`${api_url}/review-order/${detailsOrderId}`, { rating: Number(rating), review }, { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } });
      if (response.data) {
        setSubmitted(prev => ({...prev,[orderId]:true}));
        setOpenReview(prev => ({...prev,[orderId]:false}));
        toast.success("Review submitted successfully!");
      }
    } catch(e) { toast.error(e.response?.data?.message || "Failed to submit review."); }
  };

  const handleOrderClick = (order) => {
    if (!order?.razorpay_order_id) return;
    navigate("/orderdetails", { state: { orderDetails: order } });
  };

  const handleReorderClick = async (order) => {
    if (!order) return;
    try {
      const requests = order.details[0].product.map(item =>
        axios.post(`${api_url}/cart`, { product_id: item.product_id, quantity: item.quantity, variant_id: item.variant_id }, { headers: { Authorization: `Bearer ${token}` } })
      );
      const results = await Promise.allSettled(requests);
      setCartCount(prev => prev + results.filter(r => r.status === "fulfilled").length);
      navigate("/cart");
    } catch(e) { console.error("Error reordering:", e); }
  };

  const getStatusInfo = (status) => STATUS_CONFIG[status] || { label: status, color: "bg-gray-100 text-gray-600 border-gray-200", dot: "bg-gray-400" };

  return (
    <>
      <Helmet>
        <title>My Orders | MediSYS Pharmacy</title>
        <meta name="description" content="Track and manage your MediSYS Pharmacy orders." />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <div className="min-h-screen bg-[#f8fafa] font-poppins">
        {/* ── Hero strip ── */}
        <div className="relative bg-[#162c2a] overflow-hidden px-5 sm:px-8 lg:px-14 pt-8 pb-12">
          <div className="absolute inset-0 opacity-[0.04] pointer-events-none" style={{ backgroundImage: plusPattern, backgroundRepeat: "repeat", backgroundSize: "40px 40px" }} />
          <div className="absolute -top-10 right-0 w-72 h-72 rounded-full bg-[#5fbdb3]/10 blur-3xl pointer-events-none" />
          <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <span className="w-5 h-[2px] bg-[#ff601e] rounded-full" />
                <span className="text-[11px] font-semibold uppercase tracking-widest text-[#5fbdb3]">Account</span>
              </div>
              <h1 className="font-bold text-[24px] sm:text-[30px] text-white">My Orders</h1>
              {!loading && (
                <p className="text-[13px] text-white/50 mt-1">
                  {orders.length} order{orders.length !== 1 ? "s" : ""} found
                </p>
              )}
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3">
              <span className="text-[12px] font-semibold text-white/60">Filter:</span>
              <div className="relative">
                <select value={statusFilterUI} onChange={e => setStatusFilterUI(e.target.value)}
                  className="appearance-none bg-white/[0.1] border border-white/[0.15] text-white text-[12px] font-semibold rounded-xl pl-4 pr-8 h-9 outline-none focus:bg-white/[0.15] transition-all cursor-pointer">
                  {statusOptions.map(opt => <option key={opt.value} value={opt.value} className="text-[#1a2625] bg-white">{opt.label}</option>)}
                </select>
                <svg className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>
              </div>
            </div>
          </div>
        </div>

        <div className="px-5 sm:px-8 lg:px-14 py-8 -mt-4">
          {/* ── Loading ── */}
          {loading ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {Array.from({length:4}).map((_,i) => (
                <div key={i} className="bg-white rounded-2xl border border-[#e2eaea] p-5 animate-pulse">
                  <div className="flex justify-between mb-4">
                    <div className="h-4 w-40 bg-[#e2eaea] rounded-full" />
                    <div className="h-6 w-24 bg-[#e2eaea] rounded-full" />
                  </div>
                  <div className="space-y-2 mb-4">
                    <div className="h-3 w-3/4 bg-[#e2eaea] rounded-full" />
                    <div className="h-3 w-1/2 bg-[#e2eaea] rounded-full" />
                  </div>
                  <div className="h-px bg-[#e2eaea] my-4" />
                  <div className="flex justify-between">
                    <div className="h-3 w-24 bg-[#e2eaea] rounded-full" />
                    <div className="h-3 w-16 bg-[#e2eaea] rounded-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : orders.length === 0 ? (
            /* ── Empty state ── */
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#eaf4f3] border border-[#d4ecea] flex items-center justify-center">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#5fbdb3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 0 1-8 0"/>
                </svg>
              </div>
              <p className="font-semibold text-[15px] text-[#374747]">No orders yet</p>
              <p className="text-[13px] text-[#8faba9] text-center max-w-[280px]">
                You haven't placed any orders.{" "}
                <Link to="/products" className="text-[#1e4a46] font-semibold hover:text-[#ff601e] transition-colors">Browse products →</Link>
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {orders.slice(0, visibleOrders).map((order, idx) => {
                  const status = order.details[0].delivery_status;
                  const statusInfo = getStatusInfo(status);
                  const products = order.details[0].product || [];
                  const isExpanded = expandedOrders.includes(order.id);
                  const displayProducts = isExpanded ? products : products.slice(0, 2);
                  const bill = order.details[0].bill?.[0]?.totalAmount;
                  const isDelivered = status === "DELIVERED";
                  const hasReview = submitted[order.id] || order.details[0]?.OrderReview?.length > 0;
                  const existingRating = order.details[0]?.OrderReview?.[0]?.rating ?? ratings[order.id] ?? 0;

                  return (
                    <div key={idx} className="bg-white rounded-2xl border border-[#e2eaea] shadow-[0_2px_12px_rgba(22,44,42,0.07)] overflow-hidden hover:shadow-[0_4px_20px_rgba(22,44,42,0.12)] transition-shadow duration-200">
                      {/* Card top: status strip */}
                      <div className={`px-5 py-2.5 flex items-center justify-between border-b border-[#f1f5f5]`}>
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${statusInfo.dot}`} />
                          <span className={`text-[11px] font-bold px-2.5 py-0.5 rounded-full border ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                        <button onClick={() => handleOrderClick(order)} className="text-[11px] font-semibold text-[#5fbdb3] hover:text-[#1e4a46] transition-colors flex items-center gap-1">
                          View Details
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                        </button>
                      </div>

                      {/* Card body */}
                      <div className="p-5">
                        {/* Products list */}
                        <div className="flex flex-col gap-1.5 mb-4">
                          {displayProducts.map((item, i) => (
                            <div key={i} className="flex items-center gap-2.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-[#5fbdb3] flex-shrink-0" />
                              <p className="text-[13px] text-[#374747] leading-snug">
                                <span className="font-semibold text-[#1e4a46]">{item.variant?.product?.name}</span>
                                {item.variant?.pack_size && <span className="text-[#8faba9]"> — {item.variant.pack_size}</span>}
                              </p>
                            </div>
                          ))}
                          {products.length > 2 && (
                            <button onClick={() => toggleExpand(order.id)} className="text-[11px] font-semibold text-[#5fbdb3] hover:text-[#1e4a46] transition-colors text-left mt-0.5">
                              {isExpanded ? "Show less ↑" : `+${products.length - 2} more items`}
                            </button>
                          )}
                        </div>

                        {/* Delivery type + invoice */}
                        <div className="flex flex-wrap items-center gap-2 mb-4">
                          <span className="flex items-center gap-1.5 text-[11px] font-semibold bg-[#fff3ee] text-[#ff601e] border border-[#ffd5c2] px-2.5 py-1 rounded-full">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                              {order.details[0].delivery_type === "Store_Pickup"
                                ? <><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></>
                                : <><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></>
                              }
                            </svg>
                            {order.details[0].delivery_type === "Store_Pickup"
                              ? `Store Pickup — ${order.details[0]?.branch?.area||""}` : "Home Delivery"}
                          </span>
                          {order.details[0]?.invoice && (
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                try {
                                  const res = await fetch(order.details[0].invoice);
                                  const blob = await res.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement("a"); a.href=url; a.download="Invoice.pdf";
                                  document.body.appendChild(a); a.click();
                                  window.URL.revokeObjectURL(url); a.remove();
                                } catch(e) { console.error(e); }
                              }}
                              className="flex items-center gap-1 text-[11px] font-semibold text-[#374747] bg-[#f1f5f5] border border-[#e2eaea] px-2.5 py-1 rounded-full hover:bg-[#e2eaea] transition-colors"
                            >
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                              Invoice
                            </button>
                          )}
                        </div>

                        {/* Divider */}
                        <div className="h-px bg-[#f1f5f5] mb-4" />

                        {/* Footer row */}
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div>
                            <p className="text-[11px] text-[#8faba9]">Placed on {new Date(order?.created_at).toLocaleDateString("en-GB",{day:"2-digit",month:"short",year:"numeric"})}</p>
                            <p className="font-bold text-[15px] text-[#1e4a46] mt-0.5">
                              ₹{bill?.toLocaleString()||0}<span className="text-[11px] text-[#8faba9] font-normal">/-</span>
                            </p>
                          </div>

                          <div className="flex items-center gap-3">
                            {/* Rating / Review */}
                            {isDelivered && (
                              hasReview ? (
                                <div className="flex items-center gap-1">
                                  {[1,2,3,4,5].map(star => (
                                    <svg key={star} width="13" height="13" viewBox="0 0 20 20" fill={star <= existingRating ? "#f59e0b" : "#d1d5db"} xmlns="http://www.w3.org/2000/svg">
                                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                    </svg>
                                  ))}
                                </div>
                              ) : (
                                <button onClick={e => { e.stopPropagation(); toggleReviewDropdown(order.id); }}
                                  className="flex items-center gap-1.5 text-[11px] font-semibold text-[#1e4a46] border border-[#d4ecea] bg-[#eaf4f3] px-3 h-8 rounded-xl hover:bg-[#1e4a46] hover:text-white hover:border-[#1e4a46] transition-all duration-200">
                                  <svg width="11" height="11" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                                  Rate & Review
                                </button>
                              )
                            )}

                            {/* Reorder */}
                            <button onClick={() => handleReorderClick(order)}
                              className="flex items-center gap-1.5 text-[11px] font-semibold text-white bg-[#162c2a] hover:bg-[#1e4a46] px-3 h-8 rounded-xl transition-all duration-200 shadow-[0_2px_6px_rgba(22,44,42,0.25)]">
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.84"/></svg>
                              Reorder
                            </button>
                          </div>
                        </div>

                        {/* Review panel */}
                        {isDelivered && openReview[order.id] && !submitted[order.id] && (
                          <div className="mt-4 pt-4 border-t border-[#f1f5f5]" onClick={e => e.stopPropagation()}>
                            <div className="bg-[#f8fafa] border border-[#e2eaea] rounded-xl p-4 flex flex-col gap-3">
                              <div className="flex items-center gap-3">
                                <span className="text-[12px] font-semibold text-[#374747]">Your Rating:</span>
                                <div className="flex gap-1">
                                  {[1,2,3,4,5].map(star => (
                                    <button key={star} onClick={e => { e.stopPropagation(); handleStarClick(order.id, star); }}
                                      className={`w-8 h-8 rounded-xl flex items-center justify-center transition-all duration-150 ${(ratings[order.id]||0) >= star ? "bg-amber-400 text-white shadow-[0_2px_6px_rgba(245,158,11,0.4)]" : "bg-white border border-[#e2eaea] text-[#8faba9] hover:border-amber-300"}`}>
                                      <svg width="13" height="13" viewBox="0 0 20 20" fill="currentColor"><path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/></svg>
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <textarea value={reviews[order.id]||""} onChange={e => handleReviewChange(order.id, e.target.value)}
                                onClick={e => e.stopPropagation()} rows={3} placeholder="Share your experience…"
                                className="w-full border border-[#e2eaea] rounded-xl p-3 text-[13px] text-[#374747] placeholder-[#8faba9] outline-none focus:border-[#1e4a46] focus:shadow-[0_0_0_3px_rgba(95,189,179,0.15)] transition-all resize-none" />
                              <div className="flex justify-end">
                                <button
                                  onClick={e => { e.stopPropagation(); submitReview(order.id, order); }}
                                  disabled={!(ratings[order.id] && reviews[order.id]?.trim().length > 0)}
                                  className="h-9 px-4 rounded-xl text-[12px] font-semibold text-white bg-[#162c2a] hover:bg-[#1e4a46] transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                                  Submit Review
                                </button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* View more/less */}
              {orders.length > 0 && (
                <div className="flex justify-center mt-8">
                  <button
                    onClick={() => setVisibleOrders(visibleOrders < orders.length ? orders.length : 10)}
                    className="flex items-center gap-2 h-10 px-6 rounded-xl bg-[#162c2a] hover:bg-[#1e4a46] text-white text-[13px] font-semibold transition-all duration-200 shadow-[0_2px_8px_rgba(22,44,42,0.25)]">
                    {visibleOrders < orders.length ? (
                      <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M6 9l6 6 6-6"/></svg>View More</>
                    ) : (
                      <><svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M18 15l-6-6-6 6"/></svg>View Less</>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MyOrders;